import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaFileInvoice, FaCheckCircle, FaClock, FaSync, FaCrown, FaDownload } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import EmptyState from '../components/Common/EmptyState';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';
import UpgradePrompt from '../components/Common/UpgradePrompt';
import MetricCard from '../components/Dashboard/MetricCard';

const MoneyRecovery = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  const isPro = user?.subscription?.plan === 'pro';

  useEffect(() => {
    if (isPro) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [filter, isPro]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = filter !== 'all' ? { status: filter } : {};
      
      const [casesRes, statsRes] = await Promise.all([
        api.get('/money-recovery/cases', { params }),
        api.get('/money-recovery/stats')
      ]);

      setCases(casesRes.data.data || []);
      setStats(statsRes.data.data || null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load recovery cases');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      setError('');
      setSuccess('');

      const response = await api.post('/money-recovery/scan');
      
      setSuccess(`Scan completed: Found ${response.data.data.found} potential reimbursements worth $${response.data.data.total_amount.toFixed(2)}`);
      loadData();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to scan for reimbursements');
    } finally {
      setScanning(false);
    }
  };

  const handleFileCase = async (caseId) => {
    try {
      await api.post(`/money-recovery/cases/${caseId}/file`);
      setSuccess('Case filed with Amazon successfully');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to file case');
    }
  };

  const exportCases = () => {
    const headers = ['Case ID', 'Type', 'Order ID', 'Amount', 'Status', 'Date'];
    const rows = cases.map(c => [
      c.case_id || c._id,
      c.type,
      c.order_id,
      c.amount.toFixed(2),
      c.status,
      new Date(c.created_at).toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `money-recovery-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getCaseTypeLabel = (type) => {
    const labels = {
      lost_inventory: 'Lost Inventory',
      damaged_inventory: 'Damaged Inventory',
      customer_return: 'Customer Return',
      fee_error: 'Fee Error',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      filed: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (!isPro) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Money Recovery</h1>
          <div className="flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg">
            <FaCrown className="mr-2" />
            Pro Feature
          </div>
        </div>
        <UpgradePrompt
          title="Unlock Money Recovery"
          description="Automatically find and recover money from FBA lost/damaged inventory, fee errors, and customer returns"
          currentPlan="starter"
        />
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading recovery cases..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Money Recovery</h1>
          <p className="text-gray-600 mt-1">Recover money from FBA issues</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportCases}
            disabled={cases.length === 0}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </button>
          <button
            onClick={handleScan}
            disabled={scanning}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <FaSync className={`mr-2 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning...' : 'Scan for Money'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <MetricCard
            title="Total Recoverable"
            value={formatCurrency(stats.total_recoverable)}
            icon={<FaDollarSign />}
            color="green"
            subtitle={`${stats.pending_count} pending cases`}
          />
          <MetricCard
            title="Already Recovered"
            value={formatCurrency(stats.total_recovered)}
            icon={<FaCheckCircle />}
            color="blue"
            subtitle={`${stats.approved_count} approved`}
          />
          <MetricCard
            title="In Progress"
            value={stats.filed_count}
            icon={<FaClock />}
            color="orange"
            subtitle="Cases filed"
          />
          <MetricCard
            title="Success Rate"
            value={`${stats.success_rate.toFixed(1)}%`}
            icon={<FaFileInvoice />}
            color="purple"
            subtitle="Approval rate"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Cases
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('filed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'filed'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Filed
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'approved'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Approved
          </button>
        </div>
      </div>

      {/* Cases Table */}
      {cases.length === 0 ? (
        <EmptyState
          icon={<FaDollarSign className="text-6xl text-gray-400" />}
          title="No recovery cases found"
          description="Click 'Scan for Money' to find potential reimbursements from Amazon"
          actionLabel="Scan for Money"
          onAction={handleScan}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Case Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cases.map((caseItem) => (
                  <tr key={caseItem._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {caseItem.case_id || `Case #${caseItem._id.slice(-6)}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          Order: {caseItem.order_id}
                        </p>
                        {caseItem.product_id && (
                          <p className="text-xs text-gray-500">
                            ASIN: {caseItem.product_id.asin}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {getCaseTypeLabel(caseItem.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-bold text-green-600">
                        {formatCurrency(caseItem.amount)}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(caseItem.status)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">
                        {new Date(caseItem.created_at).toLocaleDateString()}
                      </p>
                      {caseItem.filed_at && (
                        <p className="text-xs text-gray-500">
                          Filed: {new Date(caseItem.filed_at).toLocaleDateString()}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {caseItem.status === 'pending' && (
                        <button
                          onClick={() => handleFileCase(caseItem._id)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          File Case
                        </button>
                      )}
                      {caseItem.status === 'approved' && (
                        <FaCheckCircle className="text-green-500 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Case Types Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Reimbursement Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="font-medium text-gray-900 mb-1">Lost Inventory</p>
            <p className="text-sm text-gray-600">Items lost by Amazon in FBA warehouses</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="font-medium text-gray-900 mb-1">Damaged Inventory</p>
            <p className="text-sm text-gray-600">Items damaged while in Amazon's possession</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="font-medium text-gray-900 mb-1">Customer Returns</p>
            <p className="text-sm text-gray-600">Items not returned but refunded to customer</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="font-medium text-gray-900 mb-1">Fee Errors</p>
            <p className="text-sm text-gray-600">Incorrect FBA fees charged by Amazon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyRecovery;
